import styled, {
  css,
  DefaultTheme,
  ThemedStyledProps
} from "styled-components";

export interface TextProps extends React.HTMLAttributes<HTMLSpanElement> {
  bold?: boolean;
  uppercase?: boolean;
  // use as keyword to switch tag + add styling
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "a" | "small" | "label" | "dd" | "dt" | "li";
  textTransform?: "uppercase" | "lowercase" | "sentence" | "capitalise";
}

export const Text = styled.span<TextProps>`
  font-family: 'europa', 'Roboto', sans-serif;
  font-style: normal;
  
  text-transform: ${({ uppercase }) => uppercase ? "uppercase" : "none"};
  letter-spacing: ${({ uppercase }) => uppercase ? "0.2rem" : "inherit"};
  cursor: ${({ onClick }) => onClick ? "pointer" : "inherit"};

  ${getFontStyle}
  ${getTextTransform}

  strong {
    font-weight: 700;
  }

  i {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }

  sub {
    font-size: 0.6em;
    vertical-align: sub;
  }

  sup {
    font-size: 0.6em;
    vertical-align: super;
  }
`;

function getFontStyle({ as, bold }: ThemedStyledProps<TextProps, DefaultTheme>) {
  switch (as) {
    case "h1":
      return css`
        font-size: 2rem;
        line-height: 1.2em;
        font-weight: 900;
        
      `;

    case "h2":
      return css`
        font-size: 1.8rem;
        line-height: 1.2em;
        font-weight: ${bold ? 700 : 400};
      `;

    case "h3":
      return css`
        font-size: 1.6rem;
        line-height: 1.2em;
        font-weight: ${bold ? 700 : 400};
      `;

    case "h4":
      return css`
        font-size: 1.4rem;
        line-height: 1.4em;
        font-weight: ${bold ? 700 : 400};
        strong {
          font-size: inherit;
          line-height: inherit;
          font-weight: 700;
        }
      `;

    case "h5":
      return css`
        font-size: 1.2rem;
        line-height: 1.4em;
        font-weight: ${bold ? 700 : 400};
        strong {
          font-size: inherit;
          line-height: inherit;
          font-weight: 700;
        }
      `;

    case "label":
      return css`
        font-size: 1.3rem;
        line-height: 1.4em;
        font-weight: ${bold ? 700 : 400};
      `;

    case "small":
      return css`
        font-size: 0.8rem;
        line-height: 1.4em;
        font-weight: ${bold ? 700 : 400};
        strong {
          font-size: inherit;
          line-height: inherit;
          font-weight: 700;
        }
      `;

    case "a":
      return css`
        font-size: 1.6rem;
        line-height: 1.4em;
        cursor: pointer;
        text-decoration: underline;
        font-weight: ${bold ? 700 : 400};
        strong {
          font-size: inherit;
          line-height: inherit;
          font-weight: 700;
        }
      `;

    case "p":
    case "span":
    case "li":
    default:
      return css`
        font-size: 1.1rem;
        line-height: 1.5em;
        font-weight: ${bold ? 700 : 400};
        strong {
          font-size: inherit;
          line-height: inherit;
          font-weight: 700;
        }
      `;
  }
}

function getTextTransform({ textTransform }: ThemedStyledProps<TextProps, DefaultTheme>) {
  switch (textTransform) {
    case "uppercase":
      return css`
        text-transform: uppercase;
      `;
    case "lowercase":
      return css`
        text-transform: lowercase;
      `;

    case "capitalise":
      return css`
        text-transform: capitalize;
      `;
    case "sentence":
      return css`
        text-transform: lowercase;
        &:first-letter {
          text-transform: capitalize;
        }
      `;

    default:
      return css`
        text-transform: none;
      `;
  }
}